<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    const dispatch = createEventDispatcher()
    import Fa from 'svelte-fa'
    import { faAngleLeft, faEdit, faHistory, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import { RecurringBillEntity } from '../../../modules/financialApi/recurring-bills/recurring-bill'
    import type { CreateRecurringBillEntity } from '../../../modules/financialApi/recurring-bills/dto/create-recurring-bill.dto'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import type { UpdateRecurringBillEntity } from '../../../modules/financialApi/recurring-bills/dto/update-recurring-bill.dto'
    import dayjs from 'dayjs'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    // ##############################################################################################################

    export let form: boolean = false
    export let recurringBillId: string = null
    export let creditDebit: EntryTypeEnum
    let entity: RecurringBillEntity = new RecurringBillEntity()

    let firstDueAt = null

    async function editEntity(id: string) {
        try {
            show = true
            step = 2
            entity = new RecurringBillEntity()
            entity = await window.api.financials.recurringBills.getOne(id)
            entity.person = await window.api.events.persons.getOne(entity.personId)
            firstDueAt = dayjs(entity.firstDueAt).format('YYYY-MM-DD')
            console.log('recurring-bill', entity)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function newEntity() {
        entity = new RecurringBillEntity()
        entity.generationAntecedenceDays = 62
        entity.intervalRange = 1
        entity.intervalType = 'month'
        step = 2
    }

    async function showHistory(id) {
        step = 3
        await loadHistory(id)
    }

    function validateDto(dto: CreateRecurringBillEntity | UpdateRecurringBillEntity) {
        if (!dto.firstDueAt) {
            window.error('Informe a data do primeiro vencimento.')
            return false
        }
        if (dto.firstDueAt.getFullYear() > new Date().getFullYear() + 3) {
            window.error('A data do primeiro vencimento não pode ser maior que 3 anos no futuro.')
            return false
        }
        if (!dto.value) {
            window.error('Informe o valor.')
            return false
        }
        if (!dto.description) {
            window.error('Informe a descrição.')
            return false
        }
        if (!dto.personId) {
            if (creditDebit === EntryTypeEnum.credit) {
                if (!confirm('É recomendado informar o pagador desta conta.\nDeseja mesmo continuar?')) return false
            } else {
                if (!confirm('É recomendado informar o recebedor desta conta.\nDeseja mesmo continuar?')) return false
            }
        }
        return true
    }

    async function createEntity() {
        try {
            show = true

            entity.firstDueAt = window.api.utils.dates.setDateStringToLocalMidnight(firstDueAt)
            const dto: CreateRecurringBillEntity = {
                creditDebit: creditDebit,
                description: entity.description,
                firstDueAt: entity.firstDueAt,
                generationAntecedenceDays: entity.generationAntecedenceDays,
                intervalRange: entity.intervalRange,
                intervalType: entity.intervalType,
                value: entity.value,
                personId: entity.person?.personId,
            }
            if (!validateDto(dto)) return
            entity = await window.api.financials.recurringBills.create(dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function sendClose() {
        dispatch('close')
    }

    async function updateEntity() {
        try {
            show = true
            entity.firstDueAt = window.api.utils.dates.setDateStringToLocalMidnight(firstDueAt)
            const dto: UpdateRecurringBillEntity = {
                description: entity.description,
                firstDueAt: entity.firstDueAt,
                generationAntecedenceDays: entity.generationAntecedenceDays,
                intervalRange: entity.intervalRange,
                intervalType: entity.intervalType,
                value: entity.value,
                personId: entity.person?.personId,
            }
            if (!validateDto(dto)) return
            await window.api.financials.recurringBills.update(entity.recurringBillId, dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (entity.recurringBillId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.financials.recurringBills.getAll({ creditDebit })
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = creditDebit === EntryTypeEnum.credit ? 'Recebimentos recorrentes' : 'Pagamentos recorrentes'
        grid.subtitle = 'Cadastre contas recorrentes que não passem pelo PDV, como o aluguel de uma sala.'
        grid.cols.push(new GridCol({ content: 'Data de criação', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Primeiro vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Descrição' }))
        grid.cols.push(new GridCol({ content: creditDebit === EntryTypeEnum.credit ? 'Pagador' : 'Recebedor' }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnHistory = new GridButton({
                callback: () => showHistory(item.recurringBillId),
                icon: faHistory,
                label: 'Histórico',
            })
            const btnEdit = new GridButton({
                callback: () => editEntity(item.recurringBillId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.firstDueAt })
            line.slots.push({ content: item.description })
            line.slots.push({ content: item.person?.name || '[não informado]' })
            line.slots.push({ content: item.value })
            line.slots.push({ content: [btnHistory, btnEdit] })
            grid.lines.push(line)
        }
    }

    let gridH: GridClass = new GridClass()
    async function loadHistory(groupId: string) {
        let history = await window.api.financials.bills.getAll({ groupId })
        history = await window.api.utils.utils.assignPersonsFromObject(history)
        console.log('history', history)
        // grid
        gridH = new GridClass()
        // grid head
        gridH.title = 'Histórico'
        gridH.subtitle = ''
        gridH.cols.push(new GridCol({ content: 'Data de criação', type: GridColTypeEnum.datetime }))
        gridH.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        gridH.cols.push(new GridCol({ content: 'Descrição' }))
        gridH.cols.push(new GridCol({ content: creditDebit === EntryTypeEnum.credit ? 'Pagador' : 'Recebedor' }))
        gridH.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        //gridH.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of history) {
            const btn = new GridButton({
                callback: () => editEntity(item.recurringBillId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.dueAt })
            line.slots.push({ content: item.description })
            line.slots.push({ content: item.person?.name || '[não informado]' })
            line.slots.push({ content: item.value })
            //line.slots.push({ content: [btn] })
            gridH.lines.push(line)
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.financials.recurringBills.remove(entity.recurringBillId)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        step = 1
    }

    let show = false
    let step = 1
    let entities: RecurringBillEntity[] = []

    onMount(async () => {
        await loadEntities()
        if (form) {
            if (recurringBillId) {
                await editEntity(recurringBillId)
            } else {
                await newEntity()
            }
        }
    })
</script>

<div>
    {#if step === 1}
        <Zact>
            <div class="spacer" />
            <button class="btn btn-primary" on:click={newEntity}><Fa icon={faPlusCircle} /> Novo</button>
        </Zact>
        <Zgrid class="m-3" {grid} />
    {/if}
    {#if step === 2}
        <Zact>
            {#if !form}
                <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            {/if}
            <div class="spacer" />
            {#if !!entity.recurringBillId}
                <button class="btn btn-warning me-2" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
            {/if}
            <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faSave} /> Salvar</button>
        </Zact>

        <Zframe title="Dados da recorrência" class="m-3">
            <div class="grid-form">
                <div class="row">
                    <div class="col">
                        <div class="input-group">
                            <label for="" class="input-group-text">Data do primeiro vencimento</label>
                            <input type="date" bind:value={firstDueAt} class="form-control" />
                        </div>
                    </div>
                    <div class="col">
                        <div class="input-group">
                            <label for="" class="input-group-text">Valor</label>
                            <input type="number" bind:value={entity.value} class="form-control" />
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <div class="input-group">
                            {#if creditDebit === EntryTypeEnum.credit}
                                <label for="" class="input-group-text">Descrição do recebimento</label>
                            {:else}
                                <label for="" class="input-group-text">Descrição do pagamento</label>
                            {/if}
                            <input type="text" bind:value={entity.description} class="form-control" />
                        </div>
                    </div>
                    <div class="col">
                        <ZentitySelect fonte="pessoa" label={creditDebit === EntryTypeEnum.credit ? 'Pagador' : 'Recebedor'} bind:value={entity.person}></ZentitySelect>
                    </div>
                </div>
            </div>
        </Zframe>

        <Zframe title="Configurações" class="m-3 mt-0">
            <div class="grid-form">
                <div class="row">
                    <div class="col">
                        <div class="input-group input-group">
                            <label for="" class="input-group-text">Tipo de intervalo</label>
                            <select class="form-select" bind:value={entity.intervalType}>
                                <option value="month">Mensal</option>
                                <option value="week">Semanal</option>
                            </select>
                        </div>
                    </div>
                    <div class="col">
                        <div class="input-group input-group">
                            <label for="" class="input-group-text">Gerar um lançamento a cada</label>
                            <select class="form-select" bind:value={entity.intervalRange}>
                                {#each Array(24) as _, i}
                                    <option value={i + 1}>{i + 1}</option>
                                {/each}
                            </select>
                            {#if entity.intervalType === 'week'}
                                <label for="" class="input-group-text">semana(s)</label>
                            {:else}
                                <label for="" class="input-group-text">mes(es)</label>
                            {/if}
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="input-group input-group">
                            <label for="" class="input-group-text">Gerar</label>
                            <select class="form-select" bind:value={entity.generationAntecedenceDays}>
                                {#each Array(365) as _, i}
                                    <option value={i + 1}>{i + 1}</option>
                                {/each}
                            </select>
                            <label for="" class="input-group-text">dias antes do vencimento</label>
                        </div>
                    </div>
                </div>
            </div>
        </Zframe>
    {/if}
    {#if step === 3}
        <Zact>
            {#if !form}
                <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            {/if}
            <div class="spacer" />
        </Zact>

        <Zgrid class="m-3" grid={gridH} />
    {/if}
</div>
